// #492 F2: what a program misses, muscle by muscle, against the RP band.
// Read off `simulateProgram`, never recounted: same sets, band and zone as the
// simulator. Under MEV: the gap and the exercises that close it. Over MRV: a
// flag, never a cut (Antenore, 03/11). The model is not retuned to fit the
// time (02/11). Pure.
import type { Exercise } from "./schemas/exercise.js";
import type { CatalogMuscleSlug } from "./muscleCatalog.js";
import type { ProgramSimulation, WeekSets } from "./programSimulation.js";
import { musclesBelowMev, type MevGap } from "./volumeLandmarks.js";
import {
  proposeForVolumeGaps,
  proposeForVolumeGapsScoped,
  type VolumeGapProposal,
} from "./volumeGapProposal.js";

/** A muscle over MRV on the cycle's average. Said, nothing proposed. */
export type MrvFlag = {
  muscle: CatalogMuscleSlug;
  weeklySets: number;
  mrv: number;
  /** The weeks over MRV, deloads left out, as the simulator reads them. */
  weeksAboveMrv: WeekSets[];
};

export type ModelFitGaps = {
  /** On the cycle's average, worst gap first. */
  belowMev: MevGap[];
  /** Furthest over MRV first. */
  aboveMrv: MrvFlag[];
  /** One per `belowMev` muscle, same order. An empty `candidates` is the
   *  honest answer, never filled from elsewhere. */
  recommendations: VolumeGapProposal[];
  /** F3: with `bodyweightOnly`, the `belowMev` muscles no bodyweight exercise
   *  trains while gear would (proposeForVolumeGapsScoped). Their
   *  recommendation stays empty. Empty without the filter. */
  noBodyweight: CatalogMuscleSlug[];
};

export const modelFitGaps = (
  simulation: Pick<ProgramSimulation, "muscles">,
  catalog: ReadonlyArray<Exercise>,
  opts: Pick<Parameters<typeof proposeForVolumeGaps>[0], "isAvailable" | "maxPerMuscle"> & {
    bodyweightOnly?: boolean;
  } = {},
): ModelFitGaps => {
  const belowMev = musclesBelowMev(
    simulation.muscles.map((m) => ({
      muscle: m.muscle,
      weeklySets: m.setsPerWeek,
      ...(m.band ? { band: m.band } : {}),
      ...(m.zone ? { zone: m.zone } : {}),
    })),
  );
  const aboveMrv = simulation.muscles
    .flatMap((m): MrvFlag[] =>
      m.band && m.zone === "above-mrv"
        ? [
            {
              muscle: m.muscle,
              weeklySets: m.setsPerWeek,
              mrv: m.band.mrv,
              weeksAboveMrv: m.weeksAboveMrv,
            },
          ]
        : [],
    )
    .sort(
      (a, b) =>
        b.weeklySets - b.mrv - (a.weeklySets - a.mrv) || a.muscle.localeCompare(b.muscle),
    );
  const { proposals, noBodyweight } = proposeForVolumeGapsScoped({ gaps: belowMev, catalog, ...opts });
  return { belowMev, aboveMrv, recommendations: proposals, noBodyweight };
};
